import {
  select,
  call,
  put,
  takeLatest,
  takeEvery,
  fork,
  all,
} from 'redux-saga/effects';
import _ from 'lodash';
import axios from 'constants/axios';
import events from 'constants/events';
import { history } from 'utils/history';
/* Actions */
import { fetchTeamWidget } from 'containers/Widgets/actions';
import { addNotification } from 'containers/Notifications/actions';

/* Selectors */

/* Constants */
import { ASSIGNEE_CHANGE_SUCCESS_MSG } from 'constants/assessmentMessages';

import {
  API_FETCH_WORKFLOW_GROUPS,
  API_GET_TEAM,
  API_UPDATE_TEAM,
  API_DELETE_TEAM,
  API_FETCH_TEAM_WIDGETS_DEFS,
  API_CREATE_PROJECT,
  API_DELETE_PROJECT,
  API_BULK_DELETE_PROJECTS,
  API_RESET_SECURITY_CODE,
  API_DISABLE_SECURITY_CODE,
  API_DOWNLOAD_TEAM_ASSESSMENT_DATA,
  API_DOWNLOAD_TEAM_ASSESSMENT_DATA_ACCESS_CHECK,
  API_IMPORT_ASSESSMENTS,
  API_FETCH_IMPORTED_ASSESSMENTS,
  API_ASSIGN_WORKFLOW_GROUP_USERS,
  API_DELETE_WORKFLOW_GROUP_USER,
  API_SEND_REMINDER,
  API_CHANGE_WORKFLOW_ASSIGNEE,
  API_SET_ASSESSMENT_IS_SENSITIVE,
  API_GET_USER_TEAM_MEMBERSHIP,
  API_TEAM_APP_ROOT,
  API_EXECUTE_TEAM_WIDGET,
  API_FETCH_SOLUTIONS_PERMISSION,
  IS_IN_ASSESSMENT_RUNTIME,
  IS_EMBEDDED_QUERY_PARAM,
  API_REFRESH_ASSESSMENT,
} from 'constants/appConstants';

/* Utils */
import { isAppEmbedded, openPageInNewTab } from 'utils/embedding';
import getTimezoneAbbr from 'utils/getTimezoneAbbr';
import { statusIsOk } from 'utils/api';
import { fetchFile } from 'utils/fetchFile';
import { getBase64 } from '../../utils/uploadFileToS3';
import {
  FETCH_TEAM,
  FETCH_TEAM_WIDGETS,
  EXECUTE_TEAM_WIDGET,
  UPDATE_TEAM,
  DELETE_TEAM,
  CREATE_ASSESSMENT,
  DELETE_ASSESSMENT,
  BULK_DELETE_ASSESSMENTS,
  REFRESH_ASSESSMENT,
  RESET_SECURITY_CODE,
  DISABLE_SECURITY_CODE,
  DOWNLOAD_TEAM_ASSESSMENT_DATA,
  IMPORT_ASSESSMENTS,
  FETCH_IMPORTED_ASSESSMENTS,
  FETCH_WORKFLOW_GROUPS,
  ASSIGN_WORKFLOW_GROUP_USERS,
  DELETE_WORKFLOW_GROUP_USER,
  CHANGE_CURRENT_ASSIGNEE,
  SEND_REMINDER,
  SET_ASSESSMENT_IS_SENSITIVE,
  FETCH_TEAM_MEMBERSHIP,
  FETCH_TEAM_SOLUTIONS,
  ADD_TEAM_SOLUTION,
  TOGGLE_ACTIVATE_TEAM_APP,
  UPDATE_TEAM_APP,
  FETCH_SOLUTIONS_PERMISSION,
} from './constants';
import {
  selectTeam,
  selectTeamID,
  selectSelectedTeamWidgetID,
  selectTeamAssessmentTerms,
  selectWidgetFilters,
} from './selectors';
import {
  fetchTeamSuccess,
  fetchTeamFailure,
  createAssessmentSuccess,
  createAssessmentFailure,
  executeTeamWidget as executeWidgetActionCreator,
  executeTeamWidgetSuccess,
  executeTeamWidgetFailure,
  updateTeamSuccess,
  updateTeamFailure,
  deleteTeamSuccess,
  deleteTeamFailure,
  fetchTeamWidgetsSuccess,
  fetchTeamWidgetsFailure,
  deleteAssessmentSuccess,
  deleteAssessmentFailure,
  refreshAssessmentSuccess,
  refreshAssessmentFailure,
  importAssessmentsSuccess,
  importAssessmentsFailure,
  fetchImportedAssessments as getImportedAssessments,
  fetchImportedAssessmentsSuccess,
  fetchImportedAssessmentsFailure,
  resetSecurityCodeSuccess,
  resetSecurityCodeFailure,
  disableSecurityCodeSuccess,
  disableSecurityCodeFailure,
  fetchWorkflowGroups as fetchWorkflowGroupsAction,
  fetchWorkflowGroupsSuccess,
  fetchWorkflowGroupsFailure,
  assignWorkflowGroupUsersSuccess,
  assignWorkflowGroupUsersFailure,
  deleteWorkflowGroupUserSuccess,
  deleteWorkflowGroupUserFailure,
  changeCurrentAssigneeFailure,
  changeCurrentAssigneeSuccess,
  sendReminderSuccess,
  sendReminderFailure,
  setAssessmentIsSensitiveSuccess,
  setAssessmentIsSensitiveFailure,
  fetchTeamSolutions as fetchTeamSolutionsAction,
  fetchTeamSolutionsSuccess,
  fetchTeamSolutionsFailure,
  addTeamSolutionSuccess,
  addTeamSolutionFailure,
  fetchTeamMembership as fetchTeamMembershipAction,
  fetchTeamMembershipSuccess,
  fetchTeamMembershipFailure,
  toggleActivateTeamAppSuccess,
  toggleActivateTeamAppFailure,
  updateTeamAppSuccess,
  updateTeamAppFailure,
  fetchSolutionsPermission as fetchSolutionsPermissionAction,
  fetchSolutionsPermissionSuccess,
  fetchSolutionsPermissionFailure,
  fetchTeamMembership,
} from './actions';
import { EntityType, processPermission } from './TeamApp/permissions';

const formatFullName = (user) => {
  const userStatus = user.status === 4 ? ' (Deactivated)' : '';
  return `${user.first_name} ${user.last_name}${userStatus}`;
};

export const formatWorkflowGroups = (groups = []) =>
  groups.map((group) => ({
    id: group.id,
    name: group.name,
    users: group.users.map((groupUser) => ({
      id: groupUser.user.user_id,
      email: groupUser.user.user_email,
      membershipID: groupUser.id,
      groupID: group.id,
      first: groupUser.user.first_name,
      last: groupUser.user.last_name,
      fullName: formatFullName(groupUser.user),
    })),
  }));

export const buildFilterQuery = (filters) => {
  if (filters.length === 0) {
    return '';
  }

  const filtersFormatted = filters
    .map((f) => `${f.columnName}.contains.${f.value}`)
    .join('|');

  return `&search=${filtersFormatted}`;
};

const formatAppPermissions = (permissions) => {
  const result = {};

  permissions.forEach((permission) => {
    result[parseInt(permission.app_id, 10)] = {
      ...processPermission(permission.permission_value),
      entityType: permission.entity_type || EntityType.GROUP,
    };
  });

  return result;
};

export function* changeWorkflowAssignee(action) {
  const teamId = yield select(selectTeamID());

  const {
    assessmentId,
    userId,
    widgetId,
    pageSize,
    currentPage,
    sorting,
    filters,
    parallelBranchUUID,
  } = action;
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_CHANGE_WORKFLOW_ASSIGNEE(assessmentId),
      data: { userIDs: userId, parallel_branch_uuid: parallelBranchUUID },
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(
          changeCurrentAssigneeSuccess(assessmentId, response.data.result)
        ),
        yield put(addNotification('success', ASSIGNEE_CHANGE_SUCCESS_MSG)),
      ]);
      const selectedWidget = yield select(selectSelectedTeamWidgetID());
      if (selectedWidget) {
        yield put(
          executeWidgetActionCreator({
            teamID: teamId,
            widgetId,
            pageSize,
            currentPage,
            sorting,
            filters,
          })
        );
      } else {
        yield put(
          fetchTeamWidget(
            teamId,
            widgetId,
            pageSize,
            currentPage,
            sorting,
            filters
          )
        );
      }

      if (action.onSuccess) {
        action.onSuccess();
      }
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(changeCurrentAssigneeFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(
          changeCurrentAssigneeFailure('Error changing workflow owner')
        ),
        yield put(addNotification('error', 'Error changing workflow owner')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(changeCurrentAssigneeFailure(err)),
      yield put(addNotification('error', 'Could not change workflow owner')),
    ]);
  }
}

export function* sendReminder(action) {
  const { assessmentId } = action;
  try {
    const response = yield call(axios, {
      method: 'get',
      url: API_SEND_REMINDER(assessmentId),
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(sendReminderSuccess()),
        yield put(addNotification('success', 'Reminders sent')),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(sendReminderFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(sendReminderFailure('Error sending reminders')),
        yield put(addNotification('error', 'Error sending reminders')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(sendReminderFailure(err)),
      yield put(addNotification('error', 'Could not send reminders')),
    ]);
  }
}

export function* setAssessmentIsSensitive(action) {
  const teamId = yield select(selectTeamID());

  const {
    assessmentId,
    widgetId,
    isSensitive,
    pageSize,
    currentPage,
    sorting,
    filters,
  } = action;
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_SET_ASSESSMENT_IS_SENSITIVE(assessmentId),
      data: {
        isSensitive,
      },
    });

    window.analytics?.track?.(events.FEATURE.IS_SENSITIVE.CLICKED);

    if (statusIsOk(response)) {
      yield all([
        yield put(setAssessmentIsSensitiveSuccess()),
        yield put(addNotification('success', 'Sensitive status set')),
      ]);
      const selectedWidget = yield select(selectSelectedTeamWidgetID());
      if (selectedWidget) {
        yield put(
          executeWidgetActionCreator({
            teamID: teamId,
            widgetId,
            pageSize,
            currentPage,
            sorting,
            filters,
          })
        );
      } else {
        yield put(
          fetchTeamWidget(
            teamId,
            widgetId,
            pageSize,
            currentPage,
            sorting,
            filters
          )
        );
      }
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(setAssessmentIsSensitiveFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(
          setAssessmentIsSensitiveFailure('Error setting sensitive status')
        ),
        yield put(addNotification('error', 'Error setting sensitive status')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(setAssessmentIsSensitiveFailure(err)),
      yield put(addNotification('error', 'Could not set sensitive status')),
    ]);
  }
}

export function* resetSecurityCode(action) {
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_RESET_SECURITY_CODE(action.teamID, action.appID),
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(
          resetSecurityCodeSuccess(
            action.appID,
            response.data.result.security_code
          )
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(resetSecurityCodeFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(resetSecurityCodeFailure('Error resetting security code')),
        yield put(addNotification('error', 'Could not reset security code')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(resetSecurityCodeFailure(err)),
      yield put(addNotification('error', 'Could not reset security code')),
    ]);
  }
}

export function* disableSecurityCode(action) {
  try {
    const response = yield call(axios, {
      method: 'delete',
      url: API_DISABLE_SECURITY_CODE(action.teamId, action.appId),
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(
          disableSecurityCodeSuccess(
            action.appId,
            response.data.result.security_code
          )
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(disableSecurityCodeFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(disableSecurityCodeFailure('Error disabling security code')),
        yield put(addNotification('error', 'Could not disable security code')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(disableSecurityCodeFailure(err)),
      yield put(addNotification('error', 'Could not disable security code')),
    ]);
  }
}

// TODO fix this to account for multi-apps
export function* fetchWorkflowGroups(action) {
  try {
    const response = yield call(axios, {
      method: 'get',
      url: API_FETCH_WORKFLOW_GROUPS(action.versionID, action.teamID),
    });

    if (statusIsOk(response)) {
      const { active, inactive } = response.data.result;
      yield all([
        yield put(
          fetchWorkflowGroupsSuccess({
            active: formatWorkflowGroups(active),
            inactive: formatWorkflowGroups(inactive),
          })
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(fetchWorkflowGroupsFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(fetchWorkflowGroupsFailure('Error fetching Workflow Groups')),
        yield put(addNotification('error', 'Error fetching Workflow Groups')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(fetchWorkflowGroupsFailure(err)),
      yield put(addNotification('error', 'Could not fetch Workflow Groups')),
    ]);
  }
}

export function* assignWorkflowGroupUsers(action) {
  const { groupID, teamID, versionID, users } = action;
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_ASSIGN_WORKFLOW_GROUP_USERS(groupID),
      data: {
        users,
        team_id: teamID,
      },
    });
    if (statusIsOk(response)) {
      yield all([
        // TODO : Rely on response to alter state instead of refetching
        yield put(fetchWorkflowGroupsAction(teamID, versionID)),
        yield put(
          assignWorkflowGroupUsersSuccess(response.data.result.wfg_user_maps)
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(assignWorkflowGroupUsersFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(
          assignWorkflowGroupUsersFailure(
            'Error assigning users to Workflow group'
          )
        ),
        yield put(
          addNotification('error', 'Error assigning users to Workflow group')
        ),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(assignWorkflowGroupUsersFailure(err)),
      yield put(
        addNotification('error', 'Could not assign users to Workflow group')
      ),
    ]);
  }
}

export function* deleteWorkflowGroupUser(action) {
  const { membershipID, groupID, selectedVersion } = action;
  const { team_id: teamID } = yield select(selectTeam());

  try {
    const response = yield call(axios, {
      method: 'delete',
      url: API_DELETE_WORKFLOW_GROUP_USER(teamID, membershipID),
    });
    if (statusIsOk(response)) {
      yield all([
        yield put(
          deleteWorkflowGroupUserSuccess(membershipID, groupID, selectedVersion)
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(deleteWorkflowGroupUserFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(
          deleteWorkflowGroupUserFailure(
            'Error deleting user from Workflow group'
          )
        ),
        yield put(
          addNotification('error', 'Error deleting user from Workflow group')
        ),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(deleteWorkflowGroupUserFailure(err)),
      yield put(
        addNotification('error', 'Could not delete user from Workflow group')
      ),
    ]);
  }
}

export function* fetchTeam(action) {
  try {
    const response = yield call(axios, {
      method: 'get',
      url: API_GET_TEAM(action.teamId),
    });

    if (statusIsOk(response) && response.data && response.data.result) {
      yield all([
        yield put(fetchTeamSolutionsAction(action.teamId)),
        yield put(fetchTeamMembershipAction(action.teamId)),
        yield put(fetchTeamSuccess(response.data.result)),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(fetchTeamFailure(response.data.error)),
        yield put(addNotification('error', 'Selected team is not available!')),
        yield call([history, history.push], '/app/teams'),
      ]);
    } else {
      yield all([
        yield put(fetchTeamFailure('Error reading response')),
        yield put(
          addNotification('error', 'Could not fetch team from the server')
        ),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(fetchTeamFailure(err)),
      yield put(
        addNotification('error', 'Could not fetch team from the server')
      ),
    ]);
  }
}

export function* fetchTeamSolutions(action) {
  try {
    const response = yield call(axios, {
      method: 'get',
      url: `team/${action.teamId}/solutions`,
    });

    if (statusIsOk(response) && response.data && response.data.result) {
      const solutions = response.data.result.map(
        (solution) => solution.solution_id
      );
      yield call(fetchTeamMembership);
      yield all([
        yield put(fetchSolutionsPermissionAction(solutions)),
        yield put(fetchTeamSolutionsSuccess(response.data.result)),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(fetchTeamSolutionsFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(fetchTeamSolutionsFailure('Error reading response')),
        yield put(
          addNotification('error', 'Could not fetch team apps from the server')
        ),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(fetchTeamSolutionsFailure(err)),
      yield put(
        addNotification('error', 'Could not fetch team apps from the server')
      ),
    ]);
  }
}

export function* fetchUserTeamMembership(action) {
  try {
    const response = yield call(axios, {
      method: 'get',
      url: API_GET_USER_TEAM_MEMBERSHIP(action.teamID),
    });
    if (statusIsOk(response)) {
      const userTeamRole = _.get(
        response.data.result,
        'member.team_group_type',
        ''
      );
      const appPermissions = _.get(response.data.result, 'appPermissions', []);
      yield all([
        yield put(
          fetchTeamMembershipSuccess({
            teamRole: userTeamRole,
            appPermissions: formatAppPermissions(appPermissions),
          })
        ),
      ]);
    } else if (response.status === 204) {
      yield all([yield put(fetchTeamMembershipSuccess({}))]);
    } else {
      const errorMessage = _.get(
        response,
        'data.error.description',
        'Could not fetch team membership information'
      );
      yield all([
        yield put(fetchTeamMembershipFailure(errorMessage)),
        yield put(addNotification('error', errorMessage)),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(fetchTeamMembershipFailure(err)),
      yield put(
        addNotification('error', 'Could not fetch team membership information')
      ),
    ]);
  }
}

export function* addTeamSolution(action) {
  try {
    const response = yield call(axios, {
      method: 'post',
      url: `team/${action.teamId}/solutions`,
      data: {
        solutionID: action.solutionId,
      },
    });
    if (statusIsOk(response)) {
      yield call(fetchUserTeamMembership, fetchTeamMembership(action.teamId));
      yield all([
        // TODO : Rely on response to alter state instead of refetching
        yield put(addTeamSolutionSuccess(response.data.result)),
        // yield put(fetchTeamSolutionsAction(action.teamId)),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(addTeamSolutionFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(addTeamSolutionFailure('Error add solution to this team')),
        yield put(addNotification('error', 'Error add solution to this team')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(addTeamSolutionFailure(err)),
      yield put(addNotification('error', 'Could not add app to this team')),
    ]);
  }
}

export function* toggleActivateTeamApp(action) {
  const { teamId, solutionId, activateType } = action;
  try {
    const response = yield call(axios, {
      method: 'put',
      url: `team/${teamId}/solution/${solutionId}/${activateType}`,
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(toggleActivateTeamAppSuccess(response.data.result)),
        yield put(fetchTeamSolutionsAction(teamId)),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(toggleActivateTeamAppFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(
          toggleActivateTeamAppFailure(`Error ${activateType} this app`)
        ),
        yield put(addNotification('error', 'Error add app to this team')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(addNotification('error', `Could not ${activateType} this app`)),
    ]);
  }
}

export function* updateTeamApp(action) {
  const { updatedTeamApp } = action;
  const { module_id: appID, team_id: teamID } = updatedTeamApp;

  try {
    const response = yield call(axios, {
      method: 'put',
      url: API_TEAM_APP_ROOT(teamID, appID),
      data: updatedTeamApp,
    });

    const payload = response.data;

    if (statusIsOk(response)) {
      yield all([yield put(updateTeamAppSuccess(payload.result))]);
    } else if (payload.error && payload.error.description) {
      yield all([
        yield put(updateTeamAppFailure(payload.error)),
        yield put(addNotification('error', payload.error.description)),
      ]);
    } else {
      yield all([
        yield put(updateTeamAppFailure('Error reading response')),
        yield put(addNotification('error', 'Could not update team app')),
      ]);
    }
  } catch (error) {
    yield all([
      yield put(updateTeamAppFailure(error)),
      yield put(addNotification('error', 'Could not update team app')),
    ]);
  }
}

export function* updateTeam(action) {
  const updatedTeam = action.updatedTeam;
  try {
    const response = yield call(axios, {
      method: 'put',
      url: API_UPDATE_TEAM(action.teamId),
      data: updatedTeam,
    });

    const payload = response.data;

    if (statusIsOk(response)) {
      yield all([yield put(updateTeamSuccess(payload.result))]);
    } else if (payload.error && payload.error.description) {
      yield all([
        yield put(updateTeamFailure(payload.error)),
        yield put(addNotification('error', payload.error.description)),
      ]);
    } else {
      yield all([
        yield put(updateTeamFailure('Error reading response')),
        yield put(addNotification('error', 'Could not update team')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(updateTeamFailure(err)),
      yield put(addNotification('error', 'Could not update team')),
    ]);
  }
}

export function* createAssessment(action) {
  const {
    name,
    teamId,
    moduleId,
    moduleVersionId,
    isOpeningInNewTab,
    hideNavBar,
  } = action;
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_CREATE_PROJECT(moduleVersionId),
      data: {
        assessment_name: name,
        team_id: teamId,
        is_preview: false,
        module_id: parseInt(moduleId, 10),
      },
    });
    if (statusIsOk(response)) {
      if (isAppEmbedded() || isOpeningInNewTab) {
        yield put(createAssessmentSuccess());
        return openPageInNewTab(
          `${
            window.location.origin
          }/app/team/${teamId}/app/${moduleId}/assessment/${
            response.data.result.project_uuid
          }?${IS_IN_ASSESSMENT_RUNTIME}${
            isAppEmbedded() ? `&${IS_EMBEDDED_QUERY_PARAM}` : ''
          }`
        );
      }
      yield all([
        yield put(createAssessmentSuccess()),
        yield call(
          [history, history.push],
          `/app/team/${teamId}/app/${moduleId}/assessment/${
            response.data.result.project_uuid
          }${hideNavBar ? `?${IS_IN_ASSESSMENT_RUNTIME}` : ''}`
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(createAssessmentFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      const { assessmentTermSingular } = yield select(
        selectTeamAssessmentTerms(moduleId)
      );
      yield all([
        yield put(createAssessmentFailure('Error reading response')),
        yield put(
          addNotification(
            'error',
            `Could not create an ${assessmentTermSingular}`
          )
        ),
      ]);
    }
  } catch (err) {
    const { assessmentTermSingular } = yield select(
      selectTeamAssessmentTerms(moduleId)
    );
    yield all([
      yield put(createAssessmentFailure(err)),
      yield put(
        addNotification(
          'error',
          `Could not create an ${assessmentTermSingular}`
        )
      ),
      yield call(
        [history, history.push],
        `/app/team/${teamId}/app/${moduleId}/assessments`
      ),
    ]);
  }
  return undefined;
}

export function* deleteTeam(action) {
  try {
    const response = yield call(axios, {
      method: 'delete',
      url: API_DELETE_TEAM(action.teamId),
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(deleteTeamSuccess()),
        yield call([history, history.push], '/app/teams'),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(deleteTeamFailure(response.data.error.description)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(deleteTeamFailure('Error reading response')),
        yield put(addNotification('error', 'Could not deactivate team')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(deleteTeamFailure(err)),
      yield put(addNotification('error', 'Could not deactivate team')),
    ]);
  }
}

export function* downloadTeamAssessmentData(action) {
  try {
    const response = yield call(axios, {
      method: 'get',
      url: API_DOWNLOAD_TEAM_ASSESSMENT_DATA_ACCESS_CHECK(
        action.teamId,
        action.appId
      ),
    });

    if (statusIsOk(response)) {
      const apiUrl = `${API_DOWNLOAD_TEAM_ASSESSMENT_DATA(
        action.teamId,
        action.appId
      )}?includeAll=${!!action.includeAll}`;

      fetchFile(apiUrl);
    } else {
      yield put(addNotification('error', 'Could not fetch team data'));
    }
  } catch (err) {
    yield put(addNotification('error', 'Could not fetch team data'));
  }
}

export function* fetchImportedAssessments(action) {
  try {
    const response = yield call(axios, {
      method: 'get',
      url: API_FETCH_IMPORTED_ASSESSMENTS(action.teamID, action.appID),
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(fetchImportedAssessmentsSuccess(response.data.result)),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(fetchImportedAssessmentsFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(
          fetchImportedAssessmentsFailure('Error fetching imported data')
        ),
        yield put(addNotification('error', 'Error fetching imported data')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(fetchImportedAssessmentsFailure(err)),
      yield put(addNotification('error', 'Could not fetch imported data')),
    ]);
  }
}

export function* importAssessments(action) {
  try {
    const { file } = action;
    const fileAsBase64 = yield getBase64(file.file);

    const response = yield call(axios, {
      method: 'post',
      url: API_IMPORT_ASSESSMENTS,
      data: {
        fileName: file.file.name,
        moduleVersionID: file.publishedVersionId,
        teamID: file.teamID,
        appID: file.appID,
        data: fileAsBase64,
      },
    });

    if (statusIsOk(response)) {
      yield all([
        yield put(importAssessmentsSuccess()),
        yield put(addNotification('success', 'Data imported successfully')),
        yield put(getImportedAssessments(file.teamID, file.appID)),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(importAssessmentsFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(importAssessmentsFailure('Error importing data')),
        yield put(addNotification('error', 'Error importing data')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(importAssessmentsFailure(err)),
      yield put(addNotification('error', 'Could not import data')),
    ]);
  }
}

export function* deleteAssessment(action) {
  try {
    const response = yield call(axios, {
      method: 'delete',
      url: API_DELETE_PROJECT(action.assessmentId),
    });

    if (response.status === 200) {
      const { teamID, widgetID, pageSize, currentPage, sorting, filters } =
        action;
      yield all([
        yield put(
          executeWidgetActionCreator({
            teamID,
            widgetID,
            pageSize,
            currentPage,
            sorting,
            filters,
          })
        ),
        yield put(deleteAssessmentSuccess()),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(deleteAssessmentFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(deleteAssessmentFailure('Error reading response')),
        yield put(addNotification('error', 'Could not delete entity')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(deleteAssessmentFailure(err)),
      yield put(addNotification('error', 'Could not delete entity')),
    ]);
  }
}

export function* bulkDeleteAssessments(action) {
  try {
    const {
      allSelected,
      assessmentIds: actionAssessmentIds,
      teamID,
      widgetID,
    } = action;
    const assessmentIds = allSelected ? [] : actionAssessmentIds;

    const response = yield call(axios, {
      method: 'delete',
      url: API_BULK_DELETE_PROJECTS(),
      data: {
        allSelected,
        assessmentIds,
        teamId: teamID,
        widgetId: widgetID,
      },
    });

    if (response.status === 200) {
      const { pageSize, currentPage, sorting, filters } = action;
      yield all([
        yield put(
          executeWidgetActionCreator({
            teamID,
            widgetID,
            pageSize,
            currentPage,
            sorting,
            filters,
          })
        ),
        yield put(deleteAssessmentSuccess()),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(deleteAssessmentFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(deleteAssessmentFailure('Error reading response')),
        yield put(addNotification('error', 'Could not delete entity')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(deleteAssessmentFailure(err)),
      yield put(addNotification('error', 'Could not delete entity')),
    ]);
  }
}

export function* refreshAssessment(action) {
  try {
    const response = yield call(axios, {
      method: 'POST',
      url: API_REFRESH_ASSESSMENT(action.assessmentId),
    });

    if (response.status === 200) {
      yield all([
        yield put(refreshAssessmentSuccess()),
        yield put(
          addNotification(
            'success',
            'Refreshing assessment. This may take some time'
          )
        ),
      ]);
    } else if (response.data.error && response.data.error.description) {
      yield all([
        yield put(refreshAssessmentFailure(response.data.error)),
        yield put(addNotification('error', response.data.error.description)),
      ]);
    } else {
      yield all([
        yield put(refreshAssessmentFailure('Error reading response')),
        yield put(addNotification('error', 'Could not refresh entity')),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(refreshAssessmentFailure(err)),
      yield put(addNotification('error', 'Could not refresh entity')),
    ]);
  }
}

export function* fetchTeamWidgets(action) {
  try {
    const { teamID, appID, widgetID } = action;
    const response = yield call(axios, {
      method: 'get',
      url: API_FETCH_TEAM_WIDGETS_DEFS(teamID, appID),
    });

    if (statusIsOk(response)) {
      yield all([put(fetchTeamWidgetsSuccess(response.data.result, widgetID))]);
    } else if (response.status === 204) {
      yield all([put(fetchTeamWidgetsSuccess([]))]);
    } else {
      yield all([put(fetchTeamWidgetsFailure(response.data.error))]);
    }
  } catch (error) {
    const errorText = 'Could not fetch team widgets';
    yield all([
      put(fetchTeamWidgetsFailure(errorText)),
      put(addNotification('error', errorText)),
    ]);
  }
}

export const buildWidgetsQuery = (pageSize, currentPage, sorting, filters) => {
  let sortingQuery = '';
  let filteringQuery = '';

  if (!_.isEmpty(sorting)) {
    sortingQuery = `&sort_by=${sorting[0].columnName}.${sorting[0].direction}`;
  }

  if (!_.isEmpty(filters)) {
    filteringQuery = buildFilterQuery(filters);
  }

  return `?offset=${
    pageSize * currentPage
  }&limit=${pageSize}${sortingQuery}${filteringQuery}&timezone=${_.lowerCase(
    getTimezoneAbbr()
  )}`;
};

// TODO: use Widgets container version
export function* executeTeamWidget(action) {
  try {
    const selectedWidget = yield select(selectSelectedTeamWidgetID());
    const customFilters = yield select(selectWidgetFilters());

    const { teamID, widgetID, pageSize, currentPage, sorting, filters } =
      action;

    const response = yield call(axios, {
      method: 'post',
      url: `${API_EXECUTE_TEAM_WIDGET(
        teamID,
        widgetID || selectedWidget
      )}${buildWidgetsQuery(pageSize, currentPage, sorting, filters)}`,
      data: customFilters,
    });

    if (statusIsOk(response)) {
      yield all([
        put(
          executeTeamWidgetSuccess(
            response.data.result.executedWidget,
            response.data.result.definition
          )
        ),
      ]);
    } else {
      yield all([put(executeTeamWidgetFailure(response.data.error))]);
    }
  } catch (error) {
    const errorText = 'Could not execute a widget';

    yield all([
      put(executeTeamWidgetFailure(errorText)),
      put(addNotification('error', errorText)),
    ]);
  }
}

export function* fetchSolutionsPermission(action) {
  try {
    const response = yield call(axios, {
      method: 'post',
      url: API_FETCH_SOLUTIONS_PERMISSION(),
      data: {
        solutions: action.solutions,
      },
    });
    if (statusIsOk(response)) {
      yield put(fetchSolutionsPermissionSuccess(response.data.result));
    } else {
      const errorMessage = _.get(
        response,
        'data.error.description',
        'Could not fetch solution permissions information'
      );
      yield all([
        yield put(fetchSolutionsPermissionFailure(errorMessage)),
        yield put(addNotification('error', errorMessage)),
      ]);
    }
  } catch (err) {
    yield all([
      yield put(fetchTeamMembershipFailure(err)),
      yield put(
        addNotification(
          'error',
          'Could not fetch solution permissions information'
        )
      ),
    ]);
  }
}

export function* fetchTeamWatcher() {
  yield takeLatest(FETCH_TEAM, fetchTeam);
}

export function* addTeamSolutionWatcher() {
  yield takeLatest(ADD_TEAM_SOLUTION, addTeamSolution);
}

export function* fetchTeamSolutionsWatcher() {
  yield takeLatest(FETCH_TEAM_SOLUTIONS, fetchTeamSolutions);
}

export function* toggleActivateTeamAppWatcher() {
  yield takeLatest(TOGGLE_ACTIVATE_TEAM_APP, toggleActivateTeamApp);
}

export function* fetchUserTeamMembershipWatcher() {
  yield takeLatest(FETCH_TEAM_MEMBERSHIP, fetchUserTeamMembership);
}

export function* createAssessmentWatcher() {
  yield takeEvery(CREATE_ASSESSMENT, createAssessment);
}

export function* updateTeamWatcher() {
  yield takeLatest(UPDATE_TEAM, updateTeam);
}

export function* deleteTeamWatcher() {
  yield takeLatest(DELETE_TEAM, deleteTeam);
}

export function* deleteAssessmentWatcher() {
  yield takeEvery(DELETE_ASSESSMENT, deleteAssessment);
}

export function* bulkDeleteAssessmentsWatcher() {
  yield takeEvery(BULK_DELETE_ASSESSMENTS, bulkDeleteAssessments);
}

export function* refreshAssessmentWatcher() {
  yield takeEvery(REFRESH_ASSESSMENT, refreshAssessment);
}

export function* resetSecurityCodeWatcher() {
  yield takeLatest(RESET_SECURITY_CODE, resetSecurityCode);
}

export function* disableSecurityCodeWarcher() {
  yield takeLatest(DISABLE_SECURITY_CODE, disableSecurityCode);
}

export function* downloadTeamAssessmentDataWatcher() {
  yield takeLatest(DOWNLOAD_TEAM_ASSESSMENT_DATA, downloadTeamAssessmentData);
}

export function* importAssessmentsWatcher() {
  yield takeLatest(IMPORT_ASSESSMENTS, importAssessments);
}

export function* fetchImportedAssessmentsWatcher() {
  yield takeLatest(FETCH_IMPORTED_ASSESSMENTS, fetchImportedAssessments);
}

export function* fetchWorkflowGroupsWatcher() {
  yield takeLatest(FETCH_WORKFLOW_GROUPS, fetchWorkflowGroups);
}

export function* assignWorkflowGroupUsersWatcher() {
  yield takeLatest(ASSIGN_WORKFLOW_GROUP_USERS, assignWorkflowGroupUsers);
}

export function* deleteWorkflowGroupUserWatcher() {
  yield takeLatest(DELETE_WORKFLOW_GROUP_USER, deleteWorkflowGroupUser);
}

export function* fetchTeamWidgetsWatcher() {
  yield takeLatest(FETCH_TEAM_WIDGETS, fetchTeamWidgets);
}

export function* executeTeamWidgetWatcher() {
  yield takeLatest(EXECUTE_TEAM_WIDGET, executeTeamWidget);
}

export function* changeWorkflowAssigneeWatcher() {
  yield takeLatest(CHANGE_CURRENT_ASSIGNEE, changeWorkflowAssignee);
}

export function* sendReminderWatcher() {
  yield takeLatest(SEND_REMINDER, sendReminder);
}

export function* setAssessmentIsSensitiveWatcher() {
  yield takeLatest(SET_ASSESSMENT_IS_SENSITIVE, setAssessmentIsSensitive);
}

export function* updateTeamAppWatcher() {
  yield takeLatest(UPDATE_TEAM_APP, updateTeamApp);
}

export function* fetchSolutionsPermissionWatcher() {
  yield takeLatest(FETCH_SOLUTIONS_PERMISSION, fetchSolutionsPermission);
}

// Individual exports for testing
export function* rootSaga() {
  yield all([
    fork(executeTeamWidgetWatcher),
    fork(fetchTeamWatcher),
    fork(fetchTeamSolutionsWatcher),
    fork(updateTeamWatcher),
    fork(deleteTeamWatcher),
    fork(createAssessmentWatcher),
    fork(deleteAssessmentWatcher),
    fork(bulkDeleteAssessmentsWatcher),
    fork(refreshAssessmentWatcher),
    fork(resetSecurityCodeWatcher),
    fork(disableSecurityCodeWarcher),
    fork(downloadTeamAssessmentDataWatcher),
    fork(importAssessmentsWatcher),
    fork(fetchImportedAssessmentsWatcher),
    fork(fetchTeamWidgetsWatcher),
    fork(fetchWorkflowGroupsWatcher),
    fork(assignWorkflowGroupUsersWatcher),
    fork(deleteWorkflowGroupUserWatcher),
    fork(changeWorkflowAssigneeWatcher),
    fork(sendReminderWatcher),
    fork(setAssessmentIsSensitiveWatcher),
    fork(fetchUserTeamMembershipWatcher),
    fork(addTeamSolutionWatcher),
    fork(toggleActivateTeamAppWatcher),
    fork(updateTeamAppWatcher),
    fork(fetchSolutionsPermissionWatcher),
  ]);
}

// All sagas to be loaded
export default [rootSaga];
